import {default as cx} from "classnames"
import moment from "moment"
import {Skeleton} from "primereact/skeleton"

import {dynamicSort} from "../utilities/general"

export const getCellCustom = (col, row, extras, page) => {
    if (!row) {
        // Handle the case where row is null (e.g., during sorting)
        return ""
    }
    if (row.loading) {
        return <Skeleton className="p-0 m-0 h-40px" />
    }
    const func = COLUMNS[col].func
    return func(row, extras?.[col], page)
}

const sortFunction = (fieldFunction) => {
    return ({data, field, order}) => {
        if (fieldFunction) {
            field = fieldFunction()
        }
        if (order === 1) {
            // Ascending
            return data.sort(dynamicSort(field, false))
        } else if (order === -1) {
            // Descending
            return data.sort(dynamicSort(field, true))
        }
        return data
    }
}

function bgPct(col) {
    return col > 80
        ? "bg-light-success"
        : col > 60
          ? "bg-light-primary"
          : col > 40
            ? "bg-light-warning"
            : "bg-light-danger"
}

export const readinessMapping = {
    1: {text: "Lots of Learning Left", badge: "badge-light-danger"},
    2: {text: "Making Progress", badge: "badge-light-warning"},
    3: {text: "Almost Done", badge: "badge-light-primary"},
    4: {text: "Ready for Practice", badge: "badge-light-success"},
}

export const COLUMNS = {
    name_location: {
        header: () => "Name & Location",
        func: (row) => {
            return (
                <div className="d-flex flex-column gap-2">
                    <div className="fw-bold">{row.name}</div>
                    <div>{row.location}</div>
                </div>
            )
        },
        sortable: false,
        sortField: ["name", "location"],
        sortFunction: sortFunction(),
    },
    experience: {
        header: () => "Prior Experience",
        func: (row) => {
            return row.experience
        },
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    readiness: {
        header: () => "Current Readiness",
        func: (row) => {
            const mapped = readinessMapping[row.current_readiness_rating]
            if (mapped) {
                return <div className={cx("badge px-4 py-3", mapped.badge)}>{mapped.text}</div>
            }
        },
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    week_of_hire: {
        header: () => "Current Week of Hire",
        cellClass: () => "text-center",
        func: (row) => {
            return row.week_of_hire
        },
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    osr_submission_status: {
        header: () => "OSR Submission Status",
        cellClass: () => "text-center",
        func: (row) => {
            return (
                <i
                    className={cx(
                        "fas fa-check-circle fs-1",
                        row.osr_submission_status ? "fa-check-circle text-success" : "fa-xmark-circle text-danger"
                    )}
                ></i>
            )
        },
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    calculated_end_date: {
        header: () => "Calculated Orientation End Date",
        func: (row) => {
            return moment(row.calculated_end_date).isValid()
                ? moment(row.calculated_end_date).format("MM/DD/YYYY")
                : null
        },
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    latest_osr_percentage: {
        header: () => "Latest % Towards Max OSR Score",
        cellClass: (row) => {
            return cx("text-center", row.latest_osr_percentage && bgPct(row.latest_osr_percentage))
        },
        func: (row) => {
            return row.latest_osr_percentage && `${Math.round(row.latest_osr_percentage)}%`
        },
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    latest_readiness_score: {
        header: () => "Latest Preceptor Assessed Practice Readiness Score",
        cellClass: () => "text-center",
        func: (row) => {
            return row.readiness_for_practice_rating
        },
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    days_since_nhsr: {
        header: () => "# of Days Since Last NHSR",
        cellClass: () => "text-center",
        func: (row) => {
            return row.days_since_nhsr
        },
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    nhsr_submission_status: {
        header: () => "NHSR Submission Status",
        cellClass: () => "text-center",
        func: (row) => {
            return (
                <i
                    className={cx(
                        "fas fa-check-circle fs-1",
                        row.nhsr_submission_status ? "fa-check-circle text-success" : "fa-xmark-circle text-danger"
                    )}
                ></i>
            )
        },
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    latest_support_score: {
        header: () => "Latest Support Score",
        cellClass: () => "text-center",
        func: (row) => {
            return row.latest_support_score
        },
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    latest_preceptor_score: {
        header: () => "Latest PRECEPTOR Score",
        cellClass: () => "text-center",
        func: (row) => {
            return row.latest_preceptor_score
        },
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    latest_self_score: {
        header: () => "Latest Self Assessed Practice Readiness Score",
        cellClass: () => "text-center",
        func: (row) => {
            return row.latest_self_score
        },
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    week_of_orientation: {
        header: () => "OSR Week of Orientation",
        cellClass: () => "text-center",
        func: (row) => {
            return row.week_of_orientation
        },
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    clinical_practice: {
        header: () => "Clinical Practice",
        cellClass: (row) => {
            return cx("text-center", bgPct(row.clinical_practice))
        },
        func: (row) => {
            return `${row.clinical_practice}%`
        },
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    assessment_care: {
        header: () => "Assessment & Care",
        cellClass: (row) => {
            return cx("text-center", bgPct(row.assessment_care))
        },
        func: (row) => {
            return `${row.assessment_care}%`
        },
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    prevention_safety: {
        header: () => "Prevention & Safety",
        cellClass: (row) => {
            return cx("text-center", bgPct(row.prevention_safety))
        },
        func: (row) => {
            return `${row.prevention_safety}%`
        },
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    education_collaboration: {
        header: () => "Education & Collaboration",
        cellClass: (row) => {
            return cx("text-center", bgPct(row.education_collaboration))
        },
        func: (row) => {
            return `${row.education_collaboration}%`
        },
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    professionalism: {
        header: () => "Professionalism",
        cellClass: (row) => {
            return cx("text-center", bgPct(row.professionalism))
        },
        func: (row) => {
            return `${row.professionalism}%`
        },
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    orientation_status: {
        header: () => "Orientation Status",
        func: (row) => row.orientation_status,
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    total_osrs_submitted: {
        header: () => "Total OSRS Submitted",
        func: (row) => row.total_osrs_submitted,
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    current_week_of_hire: {
        header: () => "Current Week of Hire",
        func: (row) => row.current_week_of_hire,
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    full_name: {
        header: () => "Full Name",
        func: (row) => row.full_name,
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    employee_id: {
        header: () => "Employee ID",
        func: (row) => row.employee_id,
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    email: {
        header: () => "Email",
        func: (row) => row.email,
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    role: {
        header: () => "Role",
        func: (row) => row.role,
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    position: {
        header: () => "Position",
        func: (row) => row.position,
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    location: {
        header: () => "Location",
        func: (row) => row.location,
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    hire_date: {
        header: () => "Hire Date",
        func: (row) => row.hire_date,
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    employee_fte: {
        header: () => "Employee FTE",
        func: (row) => row.employee_fte,
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    prior_experience: {
        header: () => "Prior Experience",
        func: (row) => row.prior_experience,
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
    preceptors: {
        header: () => "Preceptors",
        func: (row) => row.preceptors,
        sortable: false,
        sortField: [],
        sortFunction: sortFunction(),
    },
}
